/**
 * Task Assignee Helpers
 *
 * Normalize task assignee state while the canonical task row still stores
 * a single primary `assigned_to` value.
 *
 * Primary responsibilities:
 * - Derive stable assignee id lists from `assigned_to` and
 *   `custom_fields.assignee_ids`
 * - Merge assignee metadata into task `custom_fields` without dropping
 *   unrelated keys
 *
 * Dependencies:
 * - Losi task assignee helper
 */

import {
  isLosiTaskAssigneeId,
  normalizeLosiTaskAssigneeId
} from "@/lib/spaces/losi-task-assignee"

const TASK_TEAM_ASSIGNEE_PREFIX = "workspace-team:"
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

type TaskAssigneeSource = {
  assigned_to?: string | null
  custom_fields?: unknown
}

type TaskCustomFieldOptions = {
  assigneeIds?: string[] | null
  department?: string | null
  existingCustomFields?: unknown
  project?: string | null
  teamIds?: string[] | null
  taskType?: string | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function normalizeAssigneeId(value: unknown) {
  const losiAssigneeId = normalizeLosiTaskAssigneeId(value)

  if (losiAssigneeId) {
    return losiAssigneeId
  }

  const teamAssigneeId = normalizeTaskTeamAssigneeId(value)

  if (teamAssigneeId) {
    return teamAssigneeId
  }

  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null
}

export function buildTaskTeamAssigneeId(teamId: string) {
  return `${TASK_TEAM_ASSIGNEE_PREFIX}${teamId.trim()}`
}

export function parseTaskTeamAssigneeId(value: unknown) {
  if (typeof value !== "string") {
    return null
  }

  const trimmedValue = value.trim()

  if (!trimmedValue.startsWith(TASK_TEAM_ASSIGNEE_PREFIX)) {
    return null
  }

  const teamId = trimmedValue.slice(TASK_TEAM_ASSIGNEE_PREFIX.length).trim()
  return UUID_REGEX.test(teamId) ? teamId : null
}

export function normalizeTaskTeamAssigneeId(value: unknown) {
  const teamId = parseTaskTeamAssigneeId(value)
  return teamId ? buildTaskTeamAssigneeId(teamId) : null
}

export function isTaskTeamAssigneeId(value: unknown) {
  return Boolean(parseTaskTeamAssigneeId(value))
}

export function isVirtualTaskAssigneeId(value: unknown) {
  return isLosiTaskAssigneeId(value) || isTaskTeamAssigneeId(value)
}

export function normalizeTaskAssigneeIds(
  input: unknown,
  primaryAssignedTo?: string | null
) {
  const normalizedIds: string[] = []
  const primaryId = normalizeAssigneeId(primaryAssignedTo)

  if (primaryId) {
    normalizedIds.push(primaryId)
  }

  if (Array.isArray(input)) {
    for (const value of input) {
      const normalizedId = normalizeAssigneeId(value)
      if (!normalizedId || normalizedIds.includes(normalizedId)) {
        continue
      }

      normalizedIds.push(normalizedId)
    }
  }

  return normalizedIds
}

export function getTaskAssigneeIds(task: TaskAssigneeSource) {
  const customAssigneeIds = isRecord(task.custom_fields)
    ? task.custom_fields.assignee_ids
    : undefined

  return normalizeTaskAssigneeIds(
    customAssigneeIds,
    task.assigned_to ?? null
  )
}

export function getPersistedPrimaryTaskAssigneeId(assigneeIds: string[]) {
  for (const assigneeId of assigneeIds) {
    if (!isVirtualTaskAssigneeId(assigneeId)) {
      return assigneeId
    }
  }

  return null
}

export function taskAssigneeIdsIncludeLosi(assigneeIds: string[]) {
  return assigneeIds.some(assigneeId => isLosiTaskAssigneeId(assigneeId))
}

export function normalizeTaskTeamIds(input: unknown) {
  if (!Array.isArray(input)) {
    return []
  }

  return Array.from(
    new Set(
      input
        .filter((value): value is string => typeof value === "string")
        .map(value => parseTaskTeamAssigneeId(value) ?? value.trim())
        .filter(Boolean)
    )
  )
}

export function getTaskTeamIds(task: TaskAssigneeSource) {
  if (!isRecord(task.custom_fields)) {
    return []
  }

  return normalizeTaskTeamIds([
    ...(Array.isArray(task.custom_fields.team_ids)
      ? task.custom_fields.team_ids
      : []),
    ...(Array.isArray(task.custom_fields.teamIds)
      ? task.custom_fields.teamIds
      : []),
    ...(typeof task.custom_fields.team_id === "string"
      ? [task.custom_fields.team_id]
      : []),
    ...(Array.isArray(task.custom_fields.assignee_ids)
      ? task.custom_fields.assignee_ids
      : [])
  ])
}

export function withTaskAssigneeIds<T extends TaskAssigneeSource>(task: T) {
  return {
    ...task,
    assignee_ids: getTaskAssigneeIds(task)
  }
}

export function buildTaskCustomFields(options: TaskCustomFieldOptions) {
  const nextFields = isRecord(options.existingCustomFields)
    ? { ...options.existingCustomFields }
    : {}

  if (options.department !== undefined) {
    nextFields.department = options.department
  }

  if (options.project !== undefined) {
    nextFields.project = options.project
  }

  if (options.taskType !== undefined) {
    nextFields.task_type = options.taskType
  }

  if (options.assigneeIds !== undefined) {
    if (options.assigneeIds.length === 0) {
      delete nextFields.assignee_ids
    } else {
      nextFields.assignee_ids = options.assigneeIds
    }
  }

  if (options.teamIds !== undefined) {
    if (options.teamIds.length === 0) {
      delete nextFields.team_ids
    } else {
      nextFields.team_ids = options.teamIds
    }
  }

  return Object.keys(nextFields).length > 0
    ? nextFields
    : null
}
